/**
 * A fresh autocomplete session id.
 *
 * Atlas declares `PredictRequest.sessionToken` as `(wix.api.format) = GUID`, so this has to be a real
 * v4 UUID rather than any old unique string — a non-GUID risks being rejected or, worse, silently
 * ignored, which would put us back to paying per keystroke while looking like we fixed it.
 *
 * `crypto.randomUUID` is the right answer and is what runs in the browser, but it is absent outside a
 * secure context and in some test environments, so there is a same-shape fallback. Neither branch needs
 * cryptographic quality: the token identifies a session for billing, it does not authorize anything.
 */
export function newPlaceSessionToken(): string {
  const cryptoRef = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoRef?.randomUUID) {
    return cryptoRef.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    // The two fixed nibbles a v4 UUID carries: version 4, and a variant of 8/9/a/b.
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}
